import React from 'react';
import { toast } from 'react-hot-toast';
import { SpreadsheetData } from '../types';
import { convertSelectedCellsToClipboard, getColumnIndex, numberToColumnLetter } from '../utils';
import { DateTime } from 'luxon';

interface UseSpreadsheetSelectionCSVExportProps {
  selectedCells: Set<string>;
  spreadsheetData: SpreadsheetData[];
  customColumnHeaders?: string[];
  filename?: string;
  includeHeaders?: boolean;
  onExportStart?: () => void;
  onExportSuccess?: () => void;
  onExportError?: (error: any) => void;
}

export function useSpreadsheetSelectionCSVExport({
  selectedCells,
  spreadsheetData,
  customColumnHeaders,
  filename,
  includeHeaders = true,
  onExportStart,
  onExportSuccess,
  onExportError
}: UseSpreadsheetSelectionCSVExportProps) {
  
  // Helper function to convert currency strings to numbers and clean up symbols
  const parseSpecialValues = React.useCallback((value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }
    
    const stringValue = String(value);
    
    // Convert checkmarks and X marks to CSV-friendly values
    if (stringValue === '✓' || stringValue === '✔') {
      return 'TRUE';
    }
    if (stringValue === '✗' || stringValue === '✘' || stringValue === '❌') {
      return 'FALSE';
    }
    
    // Convert other special symbols to CSV-friendly text
    if (stringValue.includes('⚠️')) {
      return stringValue.replace(/⚠️/g, 'WARNING:');
    }
    if (stringValue.includes('←')) {
      return stringValue.replace(/←/g, '<-');
    }
    if (stringValue.includes('→')) {
      return stringValue.replace(/→/g, '->');
    }
    
    // Check if this looks like a currency value (contains £, $, €, etc.)
    if (stringValue.match(/^[£$€¥₹₽]+[\d,.-]+$|^[\d,.-]+[£$€¥₹₽]+$/)) {
      // Remove currency symbols and parse as number
      const numericValue = stringValue.replace(/[£$€¥₹₽,]/g, '').trim();
      const parsed = parseFloat(numericValue);
      
      // Return the number if valid, otherwise return the original string
      if (!isNaN(parsed)) {
        return parsed.toString();
      }
    }
    
    return stringValue;
  }, []);

  // Helper function to escape CSV values
  const escapeCSVValue = React.useCallback((value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }
    
    // First try to parse special values (currency, symbols, etc.)
    const processedValue = parseSpecialValues(value);
    
    // If the value contains commas, quotes, or newlines, wrap it in quotes
    if (processedValue.includes(',') || processedValue.includes('"') || processedValue.includes('\n')) {
      // Escape existing quotes by doubling them
      return `"${processedValue.replace(/"/g, '""')}"`;
    }
    
    return processedValue;
  }, [parseSpecialValues]);

  // Export selected cells to CSV
  const exportSelectionToCSV = React.useCallback(async () => {
    try {
      onExportStart?.();
      
      if (!selectedCells || selectedCells.size === 0) {
        toast.error('No cells selected for export');
        return;
      }

      // Convert selected cells to 2D array using existing utility
      const selectionData = convertSelectedCellsToClipboard(selectedCells, spreadsheetData);
      
      if (selectionData.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Find the actual column indices that are being exported
      const positions = Array.from(selectedCells).map(cellKey => {
        const [rowIdx, columnKey] = cellKey.split('-');
        return { rowIdx: parseInt(rowIdx), columnKey };
      });

      // Get column indices and find the range
      const columnIndices = positions.map(p => getColumnIndex(p.columnKey));
      
      const minColIndex = Math.min(...columnIndices);
      const maxColIndex = Math.max(...columnIndices);

      const csvRows: string[] = [];

      // Add headers if requested
      if (includeHeaders) {
        let headers: string[];
        
        if (customColumnHeaders && customColumnHeaders.length > 0) {
          // Map the actual column indices to the custom headers
          headers = [];
          for (let colIndex = minColIndex; colIndex <= maxColIndex; colIndex++) {
            if (colIndex < customColumnHeaders.length) {
              headers.push(customColumnHeaders[colIndex]);
            } else {
              headers.push(`Column ${colIndex + 1}`);
            }
          }
                 } else {
           // Generate column headers based on actual column letters
           headers = [];
           for (let colIndex = minColIndex; colIndex <= maxColIndex; colIndex++) {
             const columnLetter = numberToColumnLetter(colIndex);
             headers.push(columnLetter);
           }
         }
        
        csvRows.push(headers.map(header => escapeCSVValue(header)).join(','));
      }

      // Add data rows
      selectionData.forEach(row => {
        const rowValues = row.map(cell => escapeCSVValue(cell));
        csvRows.push(rowValues.join(','));
      });

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Generate filename
      const timestamp = DateTime.utc().toISO().slice(0, 10); // YYYY-MM-DD
      const defaultFilename = `spreadsheet-selection-${timestamp}.csv`;
      const finalFilename = filename || defaultFilename;

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = finalFilename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      window.URL.revokeObjectURL(url);

      const cellCount = selectedCells.size;
      const rowCount = selectionData.length;
      const colCount = selectionData[0]?.length || 0;
      
      toast.success(`Exported ${cellCount} cells (${rowCount} × ${colCount}) to CSV`);
      onExportSuccess?.();
      
    } catch (error) {
      console.error('Error exporting selection to CSV:', error);
      toast.error('Failed to export selection to CSV');
      onExportError?.(error);
    }
  }, [
    selectedCells,
    spreadsheetData,
    customColumnHeaders,
    filename,
    includeHeaders,
    escapeCSVValue,
    parseSpecialValues,
    onExportStart,
    onExportSuccess,
    onExportError
  ]);

  return {
    exportSelectionToCSV
  };
} 